import { Provider } from 'react-redux';
import { store } from './store';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import SuggestionTable from './components/SuggestionTable';
import CoordinatorPage from './components/CoordinatorPage';
import SelectionPage from './components/SelectionPage';
import SettingsPage from './components/SettingsPage';
import ProtectionPage from './components/ProtectionPage';
import LoadingOverlay from './components/LoadingOverlay';
import ErrorToast from './components/ErrorToast';
import ErrorBoundary from './components/ErrorBoundary';
import { useAppDispatch, useAppSelector } from './hooks';
import { fetchOffers, fetchOffer, fetchOfferRows, fetchOfferColumns, setSelectedCode } from './store/offersSlice';
import { fetchReferences } from './store/referencesSlice';
import { useState, useEffect } from 'preact/hooks';
import { useWebSocket } from './hooks/useWebSocket';
import type { INavbarProps } from './types';

function AppContent() {
    const [unauthorized, setUnauthorized] = useState(false);

    useEffect(() => {
        // Block access outside B24 iframe
        if (window.self === window.top) {
            setUnauthorized(true);
            return;
        }
        const handler = () => setUnauthorized(true);
        window.addEventListener('b24-unauthorized', handler);
        return () => window.removeEventListener('b24-unauthorized', handler);
    }, []);

    if (unauthorized) {
        return (
            <div className="flex items-center justify-center h-screen bg-base-100">
                <div className="text-center">
                    <h1 className="text-2xl font-bold mb-2">Доступ ограничен</h1>
                    <p className="text-base-content/60">Приложение доступно только через Битрикс24</p>
                </div>
            </div>
        );
    }

    const dispatch = useAppDispatch();
    const selectedCode = useAppSelector(s => s.offers.selectedCode);
    const loading = useAppSelector(s => s.offers.loading);
    const refsLoading = useAppSelector(s => s.references.loading);
    const refsLoaded = useAppSelector(s => s.references.loaded);
    const [isSidebarOpen, setSidebarOpen] = useState(true);
    const [activeTab, setActiveTab] = useState<INavbarProps['activeTab']>('composition');

    // WebSocket realtime connection
    useWebSocket(selectedCode);

    // Load references once
    useEffect(() => {
        if (!refsLoaded && !refsLoading) {
            dispatch(fetchReferences());
        }
    }, []);

    // Load offers on mount
    useEffect(() => {
        dispatch(fetchOffers(undefined));
    }, [dispatch]);

    // Load rows+columns when offer selected; fetch offer if not in items (hidden)
    const items = useAppSelector(s => s.offers.items);
    useEffect(() => {
        if (selectedCode !== null) {
            dispatch(fetchOfferRows({ code: selectedCode }));
            dispatch(fetchOfferColumns(selectedCode));
            if (!items.find(o => Number(o.code) === selectedCode)) {
                dispatch(fetchOffer(selectedCode));
            }
        }
    }, [dispatch, selectedCode]);

    const handleClose = () => {
        dispatch(setSelectedCode(null));
    };


    return (
        <div className="flex h-screen bg-base-100 text-base-content">
            <LoadingOverlay visible={refsLoading} fullScreen text="Загрузка справочников..." />
            <ErrorToast />

            <Sidebar
                onToggleSidebar={() => setSidebarOpen(!isSidebarOpen)}
                isCollapsed={!isSidebarOpen}
            />
            <div className="flex-1 flex flex-col overflow-hidden relative">
                <LoadingOverlay visible={loading && !refsLoading} text="Загрузка..." />

                <Navbar
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    isSettingsDisabled={selectedCode === null}
                />
                <main className="flex-1 overflow-y-auto relative">
                    <ErrorBoundary key={activeTab}>
                        {activeTab === 'composition' && (
                            <div className="p-6">
                                <SuggestionTable isOpen onClose={handleClose} />
                            </div>
                        )}
                        {activeTab === 'coordinator' && <CoordinatorPage onNavigate={tab => setActiveTab(tab as any)} />}
                        {activeTab === 'selection' && <SelectionPage />}
                        {activeTab === 'protection' && <ProtectionPage />}
                        {activeTab === 'settings' && <SettingsPage />}
                    </ErrorBoundary>
                </main>
            </div>
        </div>
    );
}

export default function App() {
    return (
        <Provider store={store}>
            <AppContent />
        </Provider>
    );
}
